/**
 * API Service for Plugin Builder
 *
 * HTTP endpoints for:
 * - Requirements gathering
 * - Plugin building
 * - Draft management
 */

import { api } from '../../../core/services/api';

// Get the base URL for API calls (same as core API service)
const API_BASE = import.meta.env.VITE_BFF_BASE_URL || '';

export interface ConversationMessage {
  role: string;
  content: string;
  timestamp?: string;
}

export interface SendMessageRequest {
  message: string;
  conversation_history: ConversationMessage[];
}

export interface SendMessageResponse {
  message: string;
  information_collected: Record<string, any>;
  is_complete: boolean;
  missing_fields: string[];
  can_generate: boolean;
}

export interface PluginSpec {
  category: string;
  name: string;
  description: string;
  features: string[];
  data_requirements?: string;
  ui_preferences?: string;
  integrations: string[];
}

export interface ValidationResponse {
  is_complete: boolean;
  collected_fields: string[];
  missing_fields: string[];
  completeness_percentage: number;
  specification?: PluginSpec;
}

export interface BuildJobResponse {
  draft_id: string;
  job_id: string;
  status: string;
  message: string;
}

export interface BuildStatusResponse {
  draft_id: string;
  status: string;
  progress?: string;
  error?: string;
}

export interface PluginDraft {
  draft_id: string;
  user_id: string;
  plugin_id: string;
  name: string;
  description: string;
  status: string;
  files: Array<{
    path: string;
    content: string;
    size: number;
  }>;
  manifest?: any;
  created_at: string;
  updated_at: string;
  metadata: Record<string, any>;
}

class ApiService {
  
  // ============================================================================
  // Requirements Gathering
  // ============================================================================
  
  async startConversation(initialMessage: string = 'I want to create a plugin'): Promise<{
    conversation_id: string;
    message: string;
  }> {
    return api.post('/requirements/conversations', { initial_message: initialMessage }) as Promise<{
      conversation_id: string;
      message: string;
    }>;
  }
  
  async sendMessage(
    conversationId: string,
    request: SendMessageRequest
  ): Promise<SendMessageResponse> {
    return api.post(`/requirements/conversations/${conversationId}/messages`, request) as Promise<SendMessageResponse>;
  }

  async validateConversation(conversationId: string): Promise<ValidationResponse> {
    return api.get(`/requirements/conversations/${conversationId}/validate`) as Promise<ValidationResponse>;
  }

  async getConversation(conversationId: string): Promise<any> {
    return api.get(`/requirements/conversations/${conversationId}`);
  }
  
  // ============================================================================
  // Plugin Building
  // ============================================================================
  
  async submitRequirements(
    conversationId: string,
    skipValidation: boolean = false
  ): Promise<BuildJobResponse> {
    return api.post('/plugin-builder/submit', {
      conversation_id: conversationId,
      skip_validation: skipValidation,
    }) as Promise<BuildJobResponse>;
  }

  async getBuildStatus(draftId: string): Promise<BuildStatusResponse> {
    return api.get(`/plugin-builder/status/${draftId}`) as Promise<BuildStatusResponse>;
  }

  async retryBuild(draftId: string): Promise<BuildJobResponse> {
    return api.post(`/plugin-builder/retry/${draftId}`) as Promise<BuildJobResponse>;
  }
  
  // ============================================================================
  // Draft Management
  // ============================================================================
  
  async getDraft(draftId: string): Promise<PluginDraft> {
    // Changed to app-center endpoint
    return api.get(`/api/plugins/drafts/${draftId}`) as Promise<PluginDraft>;
  }

  async listDrafts(status?: string, limit: number = 100): Promise<PluginDraft[]> {
    // Changed to app-center endpoint - returns drafts from filesystem
    return api.get(`/api/plugins/drafts`) as Promise<PluginDraft[]>;
  }

  async deleteDraft(draftId: string): Promise<void> {
    // Changed to app-center endpoint
    return api.delete(`/api/plugins/drafts/${draftId}`) as Promise<void>;
  }

  async deployDraft(draftId: string, bundleJs: string, bundleCss?: string): Promise<void> {
    // Use publish endpoint instead (new flow)
    return api.post(`/api/plugins/drafts/${draftId}/publish`, {
      bundle_js: bundleJs,
      bundle_css: bundleCss,
    }) as Promise<void>;
  }

  // ============================================================================
  // Plugin Publishing (Multi-stage)
  // ============================================================================

  async validateDraft(draftId: string): Promise<{ success: boolean; error?: string }> {
    return api.post(`/api/plugins/drafts/${draftId}/validate`) as Promise<{ success: boolean; error?: string }>;
  }

  async checkDraftSecurity(draftId: string): Promise<{ success: boolean; error?: string }> {
    return api.post(`/api/plugins/drafts/${draftId}/security-check`) as Promise<{ success: boolean; error?: string }>;
  }

  async publishDraft(draftId: string): Promise<{ success: boolean; plugin_id?: string; error?: string }> {
    return api.post(`/api/plugins/drafts/${draftId}/publish`) as Promise<{ success: boolean; plugin_id?: string; error?: string }>;
  }
  
  // ============================================================================
  // Plugin ZIP Upload (Async with Queue)
  // ============================================================================
  
  async uploadPluginZip(file: File, onProgress?: (progress: number) => void): Promise<{
    success: boolean;
    draft_id: string;
    job_id: string;
    status: string;
    message: string;
    plugin_name?: string;
    warnings?: string[];
  }> {
    const formData = new FormData();
    formData.append('file', file);
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      // Track upload progress (only upload, not extraction)
      if (onProgress) {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = (e.loaded / e.total) * 100;
            onProgress(progress);
          }
        });
      }
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (e) {
            reject(new Error('Invalid response from server'));
          }
        } else {
          try {
            const error = JSON.parse(xhr.responseText);
            reject(new Error(error.detail || `Upload failed: ${xhr.status}`));
          } catch (e) {
            reject(new Error(`Upload failed: ${xhr.status}`));
          }
        }
      });
      
      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });
      
      xhr.addEventListener('abort', () => {
        reject(new Error('Upload cancelled'));
      });
      
      // Construct URL: use /api/plugins/upload-zip via BFF routes
      const uploadUrl = API_BASE && API_BASE.trim() !== '' && !API_BASE.startsWith('/')
        ? `${API_BASE.replace(/\/$/, '')}/plugins/upload-zip`
        : '/api/plugins/upload-zip';
      
      xhr.open('POST', uploadUrl);
      xhr.withCredentials = true; // Include cookies for session authentication

      // CSRF: the BFF CSRF middleware only trusts the X-CSRF-Token header for
      // state-changing requests (never the cookie, since cookies ride along
      // automatically). Mirror the platform pattern used by the core api client
      // and plugin SDK: read the _eid_csrf_v1 cookie and echo it as a header.
      const csrfMatch = typeof document !== 'undefined'
        ? document.cookie.match(/(?:^|;\s*)_eid_csrf_v1=([^;]+)/)
        : null;
      if (csrfMatch) {
        xhr.setRequestHeader('X-CSRF-Token', decodeURIComponent(csrfMatch[1]));
      }

      xhr.send(formData);
    });
  }
  
  // Check extraction status (reuse build status endpoint since they both use drafts)
  async getZipExtractionStatus(draftId: string): Promise<BuildStatusResponse> {
    return this.getBuildStatus(draftId);
  }
}

export const apiService = new ApiService();

